import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, Output, EventEmitter } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { AppBreadcrumbComponent } from 'src/app/layout/breadcrumb/app-breadcrumb.component';
import Swal from 'sweetalert2';

import {
    AvatarComponent,
    BadgeComponent,
    ColorModeService,
    ContainerComponent,
    DropdownComponent,
    DropdownDividerDirective,
    DropdownHeaderDirective,
    DropdownItemDirective,
    DropdownMenuDirective,
    DropdownToggleDirective,
    HeaderComponent,
    HeaderNavComponent,
    HeaderTogglerDirective,
    NavItemComponent,
    NavLinkDirective,
    ProgressBarDirective,
    ProgressComponent,
    SidebarToggleDirective,
    TextColorDirective,
    ThemeDirective
} from '@coreui/angular';

import { IconDirective } from '@coreui/icons-angular';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateDirective, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common'
import { ApiService } from 'src/app/services/api.service';

@Component({
    selector: 'app-default-header',
    templateUrl: './default-header.component.html',
    standalone: true,
    styleUrl: './default-header.component.scss',
    imports: [MatIconModule, TranslatePipe, TranslateDirective, CommonModule, BadgeComponent, TextColorDirective, ContainerComponent, HeaderTogglerDirective, SidebarToggleDirective, IconDirective, HeaderNavComponent, NavItemComponent, NavLinkDirective, RouterLink, RouterLinkActive, NgTemplateOutlet, AppBreadcrumbComponent, ThemeDirective, DropdownComponent, DropdownToggleDirective, TextColorDirective, AvatarComponent, DropdownMenuDirective, DropdownHeaderDirective, DropdownItemDirective, BadgeComponent, DropdownDividerDirective, ProgressBarDirective, ProgressComponent, NgStyle]
})
export class DefaultHeaderComponent extends HeaderComponent {

    public user: any;
    public selectedLanguage: any;
    public newNotificationCount: any;
    public newNotificationAvailable = true;
    public notifications: any = [
        { id: 0, message: "systemNotifications.welcomeMessage", link: "#", is_read: false },
    ];

    // Header avatar chip: initials derived from this.user
    public userInitials: string = '';

    readonly #colorModeService = inject(ColorModeService);
    readonly colorMode = this.#colorModeService.colorMode;

    @Output() languageChangedEvent = new EventEmitter<string>();

    readonly colorModes = [
        { name: 'light', text: 'Light', icon: 'cilSun' },
        { name: 'dark', text: 'Dark', icon: 'cilMoon' },
        { name: 'auto', text: 'Auto', icon: 'cilContrast' }
    ];

    readonly languages = [
        { name: 'en', text: 'English' },
        { name: 'ms', text: 'Malay' },
    ];

    readonly icons = computed(() => {
        const currentMode = this.colorMode();
        return this.colorModes.find(mode => mode.name === currentMode)?.icon ?? 'cilSun';
    });

    constructor(private auth: AuthenticationService, private api: ApiService, private router: Router, private translate: TranslateService) {
        super();
        this.colorMode.set('light');
    }

    ngOnInit(): void {
        this.user = this.auth.user();
        this.selectedLanguage = localStorage.getItem('language') ?? 'en';
        this.translate.use(this.selectedLanguage);

        this.userInitials = this.getInitials(this.getUserDisplayName(this.user));

        this.getSystemNotification();
    }

    /**
     * Resolves a display name from the user object. Adjust the field
     * names below to match whatever AuthenticationService.user() actually
     * returns (e.g. swap in user.name or user.full_name if those exist).
     */
    private getUserDisplayName(user: any): string {
        return (
            user?.name ||
            [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
            user?.email ||
            ''
        );
    }

    private getInitials(name: string): string {
        if (!name) return '';
        const parts = name.trim().split(/\s+/);
        const first = parts[0]?.[0] ?? '';
        const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
        return (first + last).toUpperCase();
    }

    getSystemNotification() {
        this.api.getSystemNotifications(this.user.id).subscribe((res: any) => {

            if (res.length > 0) {
                this.notifications = res;
                this.countNewNotifications();
            }
            else {
                this.newNotificationCount = 1;
            }
        });
    }

    setNotificationStatus(notification: any) {
        this.notifications.find((n: any) => n.id === notification.id).is_read = true;
        this.newNotificationCount = 0;
        this.newNotificationAvailable = false;

        if (notification.id) {
            this.api.setNotificationStatus(notification.id).subscribe((res) => {
                this.notifications = res;
                this.countNewNotifications();
            });
        }
    }

    countNewNotifications() {
        const newNotificationCount = this.notifications.filter((notification: any) => notification.is_read === false).length;

        if (newNotificationCount > 0) {
            this.newNotificationAvailable = true;
            this.newNotificationCount = newNotificationCount;
        }
        else {
            this.newNotificationAvailable = false;
            this.newNotificationCount = 0;
        }

        this.notifications = this.notifications.slice(0, 5);
    }

    setLanguage(language: any): void {
        localStorage.setItem('language', language);
        this.selectedLanguage = language;
        this.languageChangedEvent.emit(language);
        this.ngOnInit();

        // Reload page after language change
        window.location.reload();
    }

    switchRole() {
        let userType = "";
        let message = "";

        switch (this.user.user_type) {
            case "Buyer":
            case "Personal Buyer": {
                userType = "Seller"
                message = "systemNotifications.sellerRoleSwitched";
                break;
            }
            case "Seller":
            case "Personal Seller": {
                userType = "Buyer"
                message = "systemNotifications.buyerRoleSwitched";
                break;
            }
            default: {
                break;
            }
        }

        Swal.fire({
            title: "Are you sure ?",
            text: "Please confirm to switch role to " + userType + ".",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, proceed!"
        }).then((result) => {
            if (result.isConfirmed) {
                // Send system notification and email
                this.api.sendSystemNotification(this.user.id, message, '/profile').subscribe((res) => {
                    this.auth.switchRole(this.user.id).subscribe((res: any) => {
                        // clear local storage
                        localStorage.removeItem('user');
                        this.auth.toggleLogin(false);

                        //Relogin
                        localStorage.setItem('user', JSON.stringify(res));
                        this.auth.setToken(res.token);

                        // redirect to dashboard
                        this.ngOnInit();

                        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                            this.router.navigate(['/dashboard']).then(() => {
                                window.location.reload();
                            });
                        });
                    }, (err) => {
                        console.log(err)
                    })
                });
            }
        });

    }

    logout() {
        this.api.createLog('User Logout', '/logout').subscribe();

        // console.log(this.checkbox);
        this.auth.logout(true).subscribe((res) => {
            console.log(res);
            localStorage.removeItem('user');

            this.auth.toggleLogin(false);

            Swal.fire({
                position: "top-end",
                icon: "success",
                title: "User logged out.",
                showConfirmButton: false,
                timer: 1500
            }).then((result) => {
                // Redirect
                this.router.navigate(['/home']);
                window.location.reload();
            });
        }, (err) => {
            console.log(err)
        })
    }

    sidebarId = input('sidebar1');

    public newMessages = [];

    public newNotifications = [];

    public newStatus = [];

    public newTasks = [];
}